import { useEffect, useMemo, useState } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import api from "../lib/api";
import SafeImage from "../components/SafeImage";
import KineticLines from "../components/KineticLines";
import Seo from "../components/Seo";
import { newsCategoryLabel } from "../lib/labels";

/* ---------- Configuration des rubriques ---------- */
const CATEGORIES = [
  { key: "all", label: "Tous" },
  { key: "actualites", label: "Actualités" },
  { key: "romans", label: "Romans" },
  { key: "ecriture", label: "Écriture" },
  { key: "publication", label: "Publication" },
  { key: "presse", label: "Presse" },
  { key: "evenements", label: "Événements" },
  { key: "recherches", label: "Recherches" },
  { key: "coulisses", label: "Coulisses" },
];

/* Textes de présentation par onglet (fournis par l'auteur) */
const CATEGORY_CONTENT = {
  actualites: {
    title: "Actualités",
    accroche: "Les dernières nouvelles autour des romans, des publications et des projets en cours.",
    description:
      "Cette rubrique regroupe les informations récentes concernant les romans de Bruno Courtin : manuscrits en cours, corrections, couvertures, sorties prévues, recherches, événements et annonces importantes. Elle permet de suivre l'évolution des projets littéraires, depuis les premières notes d'écriture jusqu'à la publication.",
    cta: "Lire les dernières actualités",
  },
  all: {
    title: "Tous les articles",
    accroche: "L'ensemble des publications du journal de bord, toutes catégories confondues.",
    description:
      "Cette section rassemble tous les articles publiés dans le journal de bord : actualités, notes d'écriture, recherches documentaires, réflexions sur les romans, coulisses de création, informations de publication, événements et contenus liés à la presse. C'est l'espace le plus complet pour suivre l'univers littéraire de Bruno Courtin dans son ensemble.",
    cta: "Parcourir tous les articles",
  },
  romans: {
    title: "Romans",
    accroche: "Romans publiés, manuscrits en cours et univers narratifs en développement.",
    description:
      "Cette rubrique est consacrée aux romans de Bruno Courtin. Chaque roman explore un territoire différent : la mémoire, le vivant, les civilisations anciennes, la liberté intérieure, la transmission, la crise climatique ou les seuils que l'humanité doit apprendre à franchir. Les romans ne cherchent pas seulement à raconter des aventures — ils proposent des mondes, des personnages et des hypothèses qui interrogent notre époque.",
    cta: "Découvrir les romans",
  },
  ecriture: {
    title: "Écriture",
    accroche: "Notes de travail, choix narratifs, personnages et construction des récits.",
    description:
      "Cette rubrique ouvre l'atelier d'écriture. Elle rassemble les réflexions sur la construction des romans : structure des chapitres, évolution des personnages, choix de rythme, scènes déplacées, cohérence interne, corrections, titres, sous-titres et décisions narratives. Un roman ne naît pas d'un seul bloc — il se construit par reprises, déplacements, hésitations, suppressions et approfondissements.",
    cta: "Lire les notes d'écriture",
  },
  publication: {
    title: "Publication",
    accroche: "Sorties, corrections, couvertures et préparation éditoriale des ouvrages.",
    description:
      "Cette rubrique rassemble les informations liées à la publication des romans : correction, mise en page, couverture, quatrième de couverture, mots-clés, formats, publication papier ou numérique, dossiers éditeurs et annonces de sortie. Chaque livre passe par plusieurs étapes avant de trouver sa forme finale.",
    cta: "Suivre les publications",
  },
  presse: {
    title: "Presse",
    accroche: "Informations médias, dossiers de présentation, interviews et ressources presse.",
    description:
      "Cette rubrique est destinée aux journalistes, chroniqueurs, blogueurs, libraires, lecteurs professionnels et organisateurs d'événements. Elle rassemble les informations utiles autour des romans de Bruno Courtin : biographies, présentations courtes, communiqués, visuels de couverture, extraits autorisés, dossiers de presse et liens vers les articles ou interviews.",
    cta: "Accéder à l'espace presse",
  },
  evenements: {
    title: "Événements",
    accroche: "Rencontres, signatures, salons, conférences et échanges avec les lecteurs.",
    description:
      "Cette rubrique annonce les événements liés aux romans et au travail d'auteur : séances de dédicaces, salons du livre, rencontres lecteurs, conférences, lectures publiques, interventions thématiques et échanges autour des ouvrages. Les romans abordent des sujets qui peuvent nourrir de nombreuses rencontres : climat, civilisations anciennes, mémoire, vivant, transmission, résilience, psychologie et rapport à la connaissance.",
    cta: "Voir les événements",
  },
  recherches: {
    title: "Recherches",
    accroche: "Dossiers documentaires, inspirations scientifiques, historiques et symboliques.",
    description:
      "Cette rubrique rassemble les recherches menées autour des romans : données scientifiques, références historiques, hypothèses archéologiques, concepts psychologiques, observations du vivant ou éléments géographiques. Ces recherches ne servent pas à donner des leçons — elles construisent des mondes crédibles, enrichissent les personnages et donnent de la profondeur aux intrigues.",
    cta: "Explorer les recherches",
  },
  coulisses: {
    title: "Coulisses",
    accroche: "L'envers du décor : titres, couvertures, cartes, scènes supprimées et décisions de création.",
    description:
      "Cette rubrique présente les coulisses du travail d'auteur : choix des titres, couvertures, recherches graphiques, cartes, plans, scènes déplacées, personnages renommés, idées abandonnées, corrections importantes et décisions narratives. Un roman terminé donne parfois l'impression d'avoir toujours eu sa forme définitive — en réalité, chaque récit traverse plusieurs versions avant de trouver son équilibre.",
    cta: "Découvrir les coulisses",
  },
};

/* Tags thématiques suggérés — filtrage secondaire */
const THEME_TAGS = [
  "ELLE",
  "Terra Australis",
  "Mauritia",
  "NeuroAurum",
  "L'Arbre",
  "Le Feu intérieur",
  "Malo — Tome III : La Route du Soleil Levant",
  "Les Héritages du vivant",
  "Civilisations anciennes",
  "Climat",
  "Publication",
  "Couverture",
  "Recherche",
  "Écriture",
  "Coulisses",
];

/* Bloc d'introduction affiché quand un thème est sélectionné (option A : bloc en tête) */
const THEME_CONTENT = {
  "ELLE": {
    title: "ELLE",
    description: "Rubrique consacrée au roman ELLE, récit intime et mélancolique autour d'un homme, d'une présence silencieuse, de la solitude, de la dépendance, du temps qui passe, du tabac, de la maladie et des liens que l'on comprend parfois trop tard.",
  },
  "Terra Australis": {
    title: "Terra Australis",
    description: "Rubrique consacrée au roman Terra Australis, autour de l'Antarctique, d'Homo australus, des civilisations très anciennes, du Traité de 1959, du temps profond, des traces impossibles et de la crise climatique en Europe.",
  },
  "Malo — Tome III : La Route du Soleil Levant": {
    title: "Malo — Tome III : La Route du Soleil Levant",
    description: "Rubrique consacrée au troisième tome du cycle maritime de Malo, La Route du Soleil Levant. Ce thème regroupe les articles liés à la poursuite vers l'Est, aux routes maritimes de l'océan Indien, au Japon, aux secrets de l'atlas, aux héritages cartographiques, aux rivalités européennes et au destin de Malo après les événements des tomes précédents.",
  },
  "Les Héritages du vivant": {
    title: "Les Héritages du vivant",
    description: "Rubrique consacrée à l'univers global des romans liés aux peuples du vivant : Terre, Eau, Air et Feu. Ce thème regroupe les articles transversaux autour de la mémoire du vivant, des peuples anciens, des transmissions, des tatouages, des archives, de Luminia, Mauritia, NeuroAurum, Le Feu intérieur, ainsi que des grands axes symboliques : liberté intérieure, réponse consciente, mémoire, écologie, civilisation et responsabilité.",
  },
  "Mauritia": {
    title: "Mauritia",
    description:
`Mauritia est né d'une interrogation simple : comment une civilisation peut-elle disparaître alors même qu'elle possède les moyens de comprendre ce qui la menace ?

Dans ce roman, je m'intéresse à une civilisation humaine fictive installée dans l'océan Indien, sur un territoire aujourd'hui disparu ou englouti. Mauritia n'est pas seulement un lieu. C'est une mémoire. Une société raffinée, organisée autour de l'eau, de sa maîtrise, de sa circulation et de sa symbolique.

Le roman commence par une phrase qui donne le ton : « Avant que l'océan ne reprenne Mauritia, les hommes y vécurent debout. » Cette phrase contient déjà toute la tension du récit. Mauritia n'est pas un peuple primitif. Ce n'est pas une société fragile, incapable de comprendre son environnement. Au contraire, elle possède une intelligence remarquable de l'eau, des flux, des équilibres naturels et sociaux. Mais cette intelligence ne suffit pas.

Ce qui m'intéresse dans Mauritia, ce n'est pas seulement la catastrophe. C'est ce qui la précède. Le moment où une civilisation sait, mais n'agit plus. Le moment où le confort remplace l'élan, où la satisfaction devient inertie, où l'abondance finit par affaiblir le désir de vivre pleinement.

Mauritia est le peuple de l'eau. L'eau y représente la mémoire émotionnelle, la fluidité, le lien, mais aussi ce qui reprend toujours ses droits lorsque les hommes oublient les limites.

À travers Maleau, témoin de cette civilisation au bord du basculement, le lecteur découvre une société brillante, sensible, mais progressivement incapable de répondre à ce qu'elle voit venir. En parallèle, dans la partie contemporaine située en 2048, Élisa, Greg, Yeza et Olsen cherchent à comprendre ce que Mauritia peut encore nous apprendre.

Car Mauritia n'est pas seulement le récit d'un monde englouti. C'est un miroir. Un miroir tendu à nos sociétés modernes, capables de mesurer les dangers, de produire des rapports, de nommer les risques, mais souvent incapables de modifier réellement leur trajectoire.`,
  },
  "NeuroAurum": {
    title: "NeuroAurum",
    description:
`NeuroAurum est une exploration romanesque du pouvoir invisible.

Dans ce projet, je ne voulais pas écrire simplement un roman sur la technologie ou l'intelligence artificielle. Je voulais interroger ce qui se passe lorsque la technologie ne se contente plus d'aider l'être humain, mais commence à orienter ses perceptions, ses émotions, ses choix et ses récits intérieurs.

NeuroAurum est une entreprise. Mais elle est surtout une puissance narrative. Elle comprend que l'être humain ne vit pas seulement dans des faits. Il vit dans des interprétations, des mots, des images, des peurs, des croyances. Celui qui contrôle les récits ne contrôle pas seulement l'information : il influence la manière dont chacun perçoit le réel.

Dans l'univers de NeuroAurum, les systèmes d'assistance cognitive, d'analyse émotionnelle et de manipulation narrative sont devenus suffisamment subtils pour ne plus ressembler à une contrainte. Les individus croient choisir librement. Ils croient penser par eux-mêmes. Mais une partie de leur regard a déjà été organisée à leur place.

La domination moderne n'a pas toujours besoin de violence directe. Elle peut passer par le confort, la suggestion, la personnalisation, l'accompagnement permanent.

Dans cet univers, l'axe Hannō / Ōtō devient essentiel : Hannō désigne la réaction automatique issue de la blessure. Ōtō désigne la réponse consciente, créatrice et responsable. NeuroAurum tente d'exploiter les réactions. Les personnages, eux, cherchent la réponse.

Ce roman interroge donc une question très contemporaine : que devient la liberté lorsque nos récits intérieurs peuvent être anticipés, orientés ou fabriqués ? NeuroAurum n'est pas seulement une menace extérieure. C'est aussi le nom de tout ce qui, en nous, préfère parfois être guidé plutôt que libre.`,
  },
  "L'Arbre": {
    title: "L'Arbre",
    description:
`L'Arbre qui cachait la forêt est un roman sur le vivant silencieux.

Au départ, il y a un village, des familles, une ravine, des tensions humaines, des souvenirs, des sécheresses, des gestes ordinaires. Le lecteur entre dans un récit humain, presque classique. Il croit comprendre qui sont les personnages principaux.

Puis, progressivement, le regard se déplace. Ce que les humains croyaient être un décor devient une présence. Les arbres ne sont plus seulement là. Ils témoignent.

Au centre du roman se trouve Kamakata, un vieil arbre isolé, vivant à l'écart du village. Sa solitude n'est pas un choix volontaire. Elle résulte de l'histoire du lieu, de la géographie, de l'assèchement, des ruptures progressives dans les continuités souterraines. Kamakata demeure lié aux autres par des signaux faibles, par les racines, par les anciennes continuités du sol.

Autour de lui existent d'autres présences : Benoze, grand ancien plus atteint par l'assèchement ; Marinta, Nafal, Dorevan, Aveline, Talia. Ces noms portent des anagrammes, des traces cachées, comme si le langage humain devait lui aussi apprendre à écouter autrement.

Le roman refuse de faire des arbres des humains déguisés. Ils ne se déplacent pas pour se rencontrer. Ils n'ont pas des conversations comme des personnages assis autour d'une table. Leurs échanges passent par des présences, des signaux, des lenteurs, des réactions infimes, des continuités souterraines.

Comment raconter un être qui dure plus longtemps que nous ? Comment écrire une sensibilité qui ne passe ni par les mots, ni par les gestes humains, ni par l'urgence ?

L'Arbre qui cachait la forêt pose donc une question simple et vertigineuse : que verrions-nous si nous cessions de croire que seuls les humains ont une histoire ?`,
  },
  "Le Feu intérieur": {
    title: "Le Feu intérieur",
    description:
`Le Feu intérieur appartient à l'univers des peuples élémentaires, mais il peut aussi se lire comme un roman sur la colère, la mémoire et la réponse.

Après la Terre, l'Eau et l'Air, le Feu représente une dimension plus difficile : celle de l'ombre, du courage, de la blessure qui cherche une issue. Le feu peut détruire. Mais il peut aussi éclairer.

Dans ce roman, le feu n'est pas un pouvoir magique. Il ne donne pas de capacité surnaturelle. Il représente plutôt cette force intérieure qui oblige un être humain à ne plus se contenter de réagir à partir de sa blessure.

Le feu est lié à Madagascar, aux Hautes Terres, à Antananarivo, à Ambohimanga, aux mémoires orales et aux anciennes circulations entre peuples austronésiens, bantous, comoriens, arabes, indiens et asiatiques du Sud-Est.

Au cœur du récit se trouve Masina, jeune femme médiatrice culturelle et chercheuse en archives orales à Antananarivo. Masina est une femme. Assignée garçon à la naissance, elle a compris très jeune qui elle était, et sa famille a été un soutien essentiel. Son identité n'est pas un artifice narratif : elle appartient à son rapport au monde, à la parole, à la reconnaissance et à la transmission.

Le roman explore aussi les tatouages anciens. Ces marques ne lisent pas les pensées. Elles ne donnent aucun pouvoir. Elles émettent des micro-signaux bioélectromagnétiques très faibles.

La colère est au centre du roman. Colère du deuil. Colère de l'injustice. Colère contre soi. Mais le roman ne condamne pas la colère. Il cherche à comprendre ce qu'elle devient. La liberté commence lorsque l'être humain peut reconnaître sa blessure sans lui abandonner son avenir.

Le Feu intérieur est donc le roman d'un passage : celui qui mène de la réaction à la réponse.`,
  },
  "Civilisations anciennes": {
    title: "Civilisations anciennes",
    description:
`La question des civilisations anciennes traverse plusieurs de mes romans.

Elle ne repose pas sur l'envie d'affirmer des certitudes impossibles. Elle repose plutôt sur un vertige : que savons-nous réellement du passé profond ? Que reste-t-il d'une civilisation lorsque le temps a tout repris ? Et surtout, qu'appelons-nous une trace ?

Dans Terra Australis, cette interrogation prend la forme d'une hypothèse romanesque : et si une civilisation très ancienne, indépendante de l'histoire humaine connue, avait existé sur Terre il y a des dizaines de millions d'années ? Cette hypothèse n'est pas présentée comme une preuve scientifique. Elle est travaillée comme un outil de fiction, un moyen d'interroger notre arrogance d'espèce.

Nous avons tendance à penser l'évolution comme une marche vers nous. Comme si Homo sapiens était l'aboutissement naturel de l'intelligence terrestre. Mais cette représentation est peut-être d'abord une projection humaine.

Le roman s'appuie sur plusieurs idées fortes : le temps profond efface presque tout ; l'absence de trace n'est pas toujours l'absence d'existence ; une civilisation avancée pourrait ne pas laisser les traces que nous attendons ; l'intelligence ne conduit pas nécessairement à l'expansion, à la conquête ou à la visibilité.

L'Antarctique devient un lieu majeur de cette spéculation. Il est associé à la glaciation ancienne, au repère de 34,6 millions d'années, au Traité de 1959, et à l'idée d'un territoire officiellement protégé pour des raisons scientifiques et pacifiques, mais peut-être aussi pour des raisons plus secrètes dans la fiction.

Le principe narratif de Terra Australis est essentiel : il n'y aura aucune rencontre directe avec Homo australus. Le lecteur doit sentir une présence, mais cette rencontre doit être déjouée. Car une civilisation réellement avancée, si elle existe encore, n'aurait peut-être aucune raison de se montrer à une humanité qu'elle jugerait immature.

Et si nous étions bruyants, visibles, conquérants, non parce que nous sommes avancés, mais parce que nous sommes encore jeunes ?`,
  },
  "Climat": {
    title: "Climat",
    description:
`Le climat n'est pas seulement un décor dans mes romans. Il devient un révélateur.

Un révélateur de nos choix, de nos aveuglements, de nos lenteurs collectives. Le réchauffement climatique n'est pas seulement une question de températures. C'est une question de civilisation : que faisons-nous lorsque les signes deviennent visibles ? Que changeons-nous lorsque l'alerte n'est plus abstraite ?

Dans Terra Australis, j'ai souhaité intégrer une seconde intrigue centrée sur l'Europe de l'Ouest, et plus particulièrement la France et l'Alsace. Le point de départ est volontairement proche : un été où les températures extrêmes deviennent un avertissement. Une famille ouvrière alsacienne avec trois enfants se trouve confrontée à une succession de canicules de plus en plus violentes.

D'un côté, le temps profond, les civilisations anciennes, l'Antarctique, Homo australus. De l'autre, le quotidien immédiat : un appartement, une famille, des enfants, le travail, les restrictions, la chaleur, la peur de sortir.

Le climat n'est pas seulement une grande idée scientifique. Il entre dans les corps, les maisons, les horaires, les gestes. Il change la façon de dormir, de travailler, de se déplacer, de manger, de protéger les enfants.

Dans cette trajectoire romanesque, les températures augmentent progressivement jusqu'à rendre certaines sorties dangereuses, puis impossibles sans protection. Le récit explore les seuils de mortalité thermique, la gestion des épisodes à 45 °C, 50 °C, 60 °C, et la façon dont les autorités, les familles et les individus tentent de s'adapter.

Le roman s'intéresse surtout à la bascule psychologique. Au début, chacun pense que l'épisode passera. Puis l'exception devient répétition. Puis la répétition devient saison. Puis la saison devient condition d'existence.

La catastrophe n'est pas seulement dans les chiffres. Elle est dans la phrase d'un enfant qui demande pourquoi il ne peut plus sortir. Elle est dans le silence d'un père qui ne sait plus comment protéger sa famille. Elle est dans une fenêtre que l'on n'ouvre plus.`,
  },
  "Publication": {
    title: "Publication",
    description:
`La publication est une étape particulière dans la vie d'un roman.

Pendant longtemps, un livre appartient à l'auteur seul. Il existe dans les notes, les versions, les corrections, les hésitations, les images de couverture, les titres essayés puis abandonnés. Il est intime, fragile, parfois désordonné. Puis vient le moment où il doit devenir lisible par d'autres.

Publier, ce n'est pas seulement imprimer un texte. C'est accepter qu'il quitte son espace de fabrication pour rencontrer des lecteurs qui n'étaient pas là pendant son écriture.

Mes romans sont construits comme des univers reliés par des thèmes profonds : la mémoire, la transmission, la liberté intérieure, les civilisations disparues, la dépendance, l'écologie, la manipulation des récits, le rapport au vivant.

Chaque publication demande donc un travail spécifique : le manuscrit définitif, la correction, la mise en page, la couverture, la quatrième de couverture, les mots-clés, la note d'intention, le dossier éditeur, les éléments pour Amazon KDP ou pour une maison d'édition, les visuels de communication.

La publication indépendante permet une grande liberté. Elle permet de choisir la couverture, le calendrier, le format, les corrections, les mots-clés. Mais elle demande aussi de penser le livre comme un objet complet : texte, image, présentation, référencement, cohérence éditoriale.

La recherche d'éditeur obéit à une autre logique. Il faut présenter le roman clairement, sans tout expliquer, mais en donnant au comité de lecture les éléments essentiels : genre, public, résumé, originalité, note d'intention, extrait, arguments éditoriaux.

Dans les deux cas, une question demeure : comment donner au livre sa meilleure chance de rencontrer ses lecteurs ?`,
  },
  "Couverture": {
    title: "Couverture",
    description:
`La couverture est la première rencontre entre le livre et le lecteur.

Elle doit attirer sans trahir. Elle doit suggérer sans expliquer. Elle doit porter l'atmosphère du roman avant même que la première page ne soit lue.

Pour mes romans, la couverture n'est jamais un simple habillage. Elle participe à l'intention littéraire. Elle doit traduire visuellement le cœur du récit : une tension, une présence, un mystère, une émotion.

Pour ELLE, la couverture doit préserver la révélation finale. Il ne faut pas montrer clairement une cigarette. Il faut plutôt suggérer une présence, un café parisien, un homme seul, une rue humide, des volutes presque abstraites.

Pour Terra Australis, la couverture doit évoquer l'Antarctique, le mystère, la carte, l'hypothèse scientifique, sans tomber dans le spectaculaire facile.

Pour Mauritia, elle doit porter l'eau, la pierre, l'océan Indien, l'idée d'une civilisation engloutie. Elle doit évoquer la beauté avant la disparition.

Pour L'Arbre qui cachait la forêt, l'image doit rendre visible la présence végétale sans infantiliser le sujet. L'arbre ne doit pas être un personnage de conte naïf, mais un être ancien, silencieux, digne, enraciné dans un lieu.

Pour Le Feu intérieur, la couverture doit suggérer le feu comme force intérieure, non comme explosion. Le feu n'est pas une destruction spectaculaire : il est une chaleur profonde, une énergie retenue, une marque ancienne qui s'active.

Une bonne couverture doit répondre à trois exigences : identifier le genre du livre, créer une émotion immédiate, respecter le secret du roman.

La couverture est donc un seuil. Elle ne raconte pas le livre. Elle invite à y entrer.`,
  },
  "Recherche": {
    title: "Recherche",
    description:
`La recherche est la part invisible de l'écriture.

Elle ne doit pas écraser le roman, mais elle le soutient. Elle lui donne une matière, une densité, une crédibilité. Elle permet d'éviter les facilités, les incohérences, les approximations trop visibles.

Mes romans demandent souvent des recherches très différentes. Pour Mauritia, il faut explorer l'océan Indien, les hypothèses autour des terres englouties, la gestion de l'eau, les formes de déclin civilisationnel, mais aussi des références psychologiques comme Calhoun, Herzberg, Frankl, Csíkszentmihályi, Deci et Ryan, Seligman, Laborit, Jung ou Spinoza.

Pour Terra Australis, la recherche porte sur l'Antarctique, la glaciation ancienne, le Traité de 1959, les primates anciens, le temps profond, les limites de l'archéologie, mais aussi sur la manière de construire une hypothèse spéculative sans la présenter comme une vérité scientifique.

Pour Le Feu intérieur, les recherches concernent Madagascar, les Hautes Terres, Antananarivo, Ambohimanga, les circulations historiques entre peuples, les archives orales, les identités, mais aussi la colère, le deuil, la mémoire traumatique et la transformation intérieure.

Pour L'Arbre qui cachait la forêt, il faut travailler sur les arbres, leurs communications, les continuités racinaires, les signaux chimiques, les sécheresses, les espèces invasives, mais aussi sur la façon d'écrire le vivant sans anthropomorphisme excessif.

Pour NeuroAurum, la recherche porte sur l'intelligence artificielle, la désinformation, les systèmes de recommandation, les biais cognitifs, les récits politiques, les technologies d'assistance cognitive et les risques de manipulation narrative.

Mais une fois la recherche faite, il faut savoir l'oublier en partie. Un roman n'est pas un dossier documentaire. La recherche doit apparaître dans la précision des détails, dans la cohérence du monde, dans la justesse des enjeux. Elle ne doit pas prendre la place de l'émotion, des personnages ou du rythme narratif.

Le but n'est pas de montrer que l'auteur a travaillé. Le but est que le lecteur sente que le monde du roman tient debout.`,
  },
  "Écriture": {
    title: "Écriture",
    description:
`Écrire, pour moi, consiste à chercher la forme juste d'une présence.

Chaque roman commence par une intuition : une image, une phrase, un lieu, une question. Puis cette intuition demande à être habitée. Il faut trouver les personnages, leur voix, leur silence, leurs contradictions, leurs blessures, leurs gestes.

L'écriture n'est pas seulement l'invention d'une histoire. C'est une manière de regarder.

Dans ELLE, il fallait écrire la dépendance sans la nommer. Tenir jusqu'au bout l'ambiguïté. Faire croire à une femme, à une présence aimée, à une habitude de café, sans jamais tricher. Chaque phrase devait pouvoir être relue autrement après la révélation finale.

Dans L'Arbre qui cachait la forêt, il fallait apprendre à ralentir. Les arbres ne vivent pas au rythme humain. Leur présence oblige à modifier la phrase, l'attention, la temporalité.

Dans Mauritia, l'enjeu était de raconter une civilisation non par ses exploits, mais par sa fatigue intérieure. Montrer comment le confort peut devenir une ivresse, puis une immobilité.

Dans NeuroAurum, l'écriture devait suivre les mots eux-mêmes : leur pouvoir, leur ambiguïté, leur capacité à libérer ou à enfermer.

Dans Le Feu intérieur, il s'agit d'écrire la colère sans la simplifier. Ne pas la condamner trop vite. Ne pas la glorifier non plus. Montrer comment elle peut devenir destruction si elle reste blessure, ou courage si elle devient réponse.

J'accorde une grande importance à la progression lente. Les personnages ne changent pas parce qu'ils ont compris une idée. Ils changent parce qu'une situation, une perte, un amour, une maladie ou une rencontre les oblige à ne plus pouvoir fuir de la même manière.

Écrire, c'est aussi accepter que le roman sache parfois avant l'auteur ce qu'il veut dire. Le travail consiste alors à l'écouter assez longtemps pour ne pas le trahir.`,
  },
  "Coulisses": {
    title: "Coulisses",
    description: "Rubrique consacrée aux coulisses de création : titres, cartes, scènes supprimées, personnages renommés, décisions narratives et étapes invisibles qui construisent un roman.",
  },
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

/* Normalise un tag pour comparaison insensible casse/accents */
const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export default function News() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [activeTag, setActiveTag] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/news").then((r) => setItems(r.data));
  }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (cat !== "all") list = list.filter((n) => (n.category || "").toLowerCase() === cat);
    if (activeTag) list = list.filter((n) => (n.tags || []).some((t) => norm(t) === norm(activeTag)));
    return list;
  }, [items, cat, activeTag]);

  const section = CATEGORY_CONTENT[cat] || CATEGORY_CONTENT.all;

  return (
    <div>
      <Seo
        path="/actualites"
        title="Journal de bord"
        description="Journal de bord de Bruno Courtin : actualités, romans, écriture, publications, presse, événements, recherches et coulisses de création."
      />

      {/* HERO */}
      <section className="theme-ivory pt-40 pb-14 px-6 lg:px-12" data-testid="news-hero">
        <div className="max-w-[1440px] mx-auto">
          <p className="eyebrow" style={{ color: "var(--copper)" }}>Journal de bord</p>
          <h1 className="font-display text-6xl md:text-8xl lg:text-[9rem] leading-[0.9] mt-4 text-[var(--ink)]">
            <KineticLines lines={["Journal de bord"]} delay={0.1} />
          </h1>
          <p className="mt-8 max-w-3xl text-[16px] md:text-[17px] leading-[1.9] text-[var(--brown)]">
            {"Le journal de bord rassemble les actualités, recherches, notes d'écriture et coulisses des romans de Bruno Courtin. "}
            {"Il permet de suivre l'évolution des projets littéraires, depuis les premières hypothèses jusqu'à la publication. "}
            {"Chaque article ouvre une fenêtre sur l'atelier de création : construction des personnages, recherches documentaires, "}
            choix narratifs, couvertures, corrections et étapes éditoriales.
          </p>
        </div>
      </section>

      {/* NAVIGATION ONGLETS */}
      <section className="theme-ivory px-6 lg:px-12 sticky top-[72px] z-20 bg-[var(--ivory)]/95 backdrop-blur-sm">
        <div className="max-w-[1440px] mx-auto flex flex-wrap gap-x-1 gap-y-2 border-y border-[var(--line)] py-4">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => { setCat(c.key); setActiveTag(null); }}
              data-testid={`news-filter-${c.key}`}
              className={`px-4 py-2 text-[11px] font-mono uppercase tracking-widest transition-all ${
                cat === c.key
                  ? "bg-[var(--ink)] text-[var(--ivory)]"
                  : "text-[var(--brown)] hover:text-[var(--ink)]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* EN-TÊTE DE RUBRIQUE */}
      <section className="theme-ivory px-6 lg:px-12 pt-16 pb-4" data-testid={`news-section-${cat}`}>
        <div className="max-w-[1100px] mx-auto">
          <motion.div
            key={cat}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
          >
            <p className="eyebrow" style={{ color: "var(--copper)" }}>Rubrique</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-3 text-[var(--ink)] leading-[1.05]">
              {section.title}
            </h2>
            <p className="mt-5 font-display italic text-xl md:text-2xl leading-snug text-[var(--brown)] max-w-2xl">
              {section.accroche}
            </p>
            <p className="mt-6 text-[15px] md:text-[16px] leading-[1.9] text-[var(--brown)]/90 max-w-3xl">
              {section.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* FILTRE THÉMATIQUE */}
      <section className="theme-ivory px-6 lg:px-12 py-8">
        <div className="max-w-[1440px] mx-auto">
          <p className="eyebrow mb-3" style={{ color: "var(--copper)" }}>Filtrer par thème</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveTag(null)}
              data-testid="news-tag-all"
              className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 border transition-all ${
                !activeTag
                  ? "border-[var(--copper)] bg-[var(--copper)]/10 text-[var(--copper)]"
                  : "border-[var(--copper)]/30 text-[var(--brown)]/70 hover:border-[var(--copper)] hover:text-[var(--copper)]"
              }`}
            >Tous les thèmes</button>
            {THEME_TAGS.map((t) => (
              <button
                key={t}
                onClick={() => setActiveTag(activeTag === t ? null : t)}
                data-testid={`news-tag-${norm(t).replace(/\s+/g, "-")}`}
                className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 border transition-all ${
                  activeTag === t
                    ? "border-[var(--copper)] bg-[var(--copper)]/10 text-[var(--copper)]"
                    : "border-[var(--copper)]/30 text-[var(--brown)]/70 hover:border-[var(--copper)] hover:text-[var(--copper)]"
                }`}
              >{t}</button>
            ))}
          </div>
        </div>
      </section>

      {/* BLOC D'INTRODUCTION AU THÈME SÉLECTIONNÉ (option A) */}
      {activeTag && THEME_CONTENT[activeTag] && (
        <section className="theme-ivory px-6 lg:px-12 pt-6 pb-2" data-testid={`theme-intro-${norm(activeTag).replace(/\s+/g, "-")}`}>
          <div className="max-w-[1100px] mx-auto">
            <motion.div
              key={activeTag}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
              className="border-l-2 border-[var(--copper)] pl-8 py-2"
            >
              <p className="eyebrow" style={{ color: "var(--copper)" }}>Thème sélectionné</p>
              <h3 className="font-display text-3xl md:text-4xl mt-2 text-[var(--ink)] leading-tight">
                {THEME_CONTENT[activeTag].title}
              </h3>
              <div className="mt-5 text-[15px] leading-[1.9] text-[var(--brown)]/90 whitespace-pre-line max-w-3xl">
                {THEME_CONTENT[activeTag].description}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* GRILLE D'ARTICLES */}
      <section className="theme-ivory px-6 lg:px-12 pt-6 pb-16">
        <div className="max-w-[1440px] mx-auto">
          {filtered.length === 0 ? (
            <div className="border border-[var(--line)] py-20 text-center">
              <p className="font-display text-2xl text-[var(--ink)]/70">{"Aucun article dans cette rubrique pour l'instant."}</p>
              <p className="mt-3 text-[13px] font-mono uppercase tracking-widest text-[var(--brown)]/60">
                {activeTag ? `Filtre actif : ${activeTag}` : "Revenez bientôt — de nouveaux articles arrivent."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {filtered.map((n, i) => (
                <motion.article
                  key={n.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: (i % 3) * 0.08 }}
                  data-testid={`news-item-${n.id}`}
                  className="group cursor-pointer"
                  onClick={() => setOpen(n)}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[var(--ink)]">
                    {n.image_url && (
                      <SafeImage
                        src={n.image_url}
                        alt={n.title}
                        className="w-full h-full object-cover object-center transition-transform duration-1000 group-hover:scale-105"
                      />
                    )}
                  </div>
                  <div className="mt-5 flex items-center gap-3 text-[11px] font-mono uppercase tracking-widest">
                    <span style={{ color: "var(--copper)" }}>{newsCategoryLabel(n.category)}</span>
                    {n.date && <span className="text-[var(--brown)]/50">· {formatDate(n.date)}</span>}
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl mt-3 text-[var(--ink)] leading-tight">{n.title}</h3>
                  {n.summary && <p className="mt-3 text-[14px] leading-relaxed text-[var(--brown)]/80">{n.summary}</p>}
                  <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[var(--ink)] group-hover:gap-4 transition-all duration-500">
                    {"Lire l'article "}<span aria-hidden>→</span>
                  </span>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA de rubrique */}
      <section className="theme-ivory px-6 lg:px-12 pb-32">
        <div className="max-w-[1100px] mx-auto text-center border-t border-[var(--line)] pt-16">
          <p className="font-display italic text-xl md:text-2xl text-[var(--brown)] max-w-2xl mx-auto">
            {cat === "all"
              ? "Retrouvez ici tous les articles du journal de bord, du plus récent au plus ancien."
              : cat === "actualites"
              ? "Entrer dans le journal de bord, c'est suivre les romans au moment où ils se construisent."
              : section.accroche}
          </p>
          <p className="mt-6 eyebrow" style={{ color: "var(--copper)" }} data-testid={`news-cta-${cat}`}>
            {section.cta}
          </p>
        </div>
      </section>

      <AnimatePresence>
        {open && <ArticleModal news={open} onClose={() => setOpen(null)} />}
      </AnimatePresence>
    </div>
  );
}

function ArticleModal({ news, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-50 bg-[var(--ink)]/95 backdrop-blur-sm overflow-y-auto overscroll-contain"
      onClick={onClose}
      data-lenis-prevent
      data-testid="article-modal"
    >
      <div className="min-h-screen py-20 px-6 lg:px-12" data-lenis-prevent>
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 20, opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="max-w-[1100px] mx-auto theme-ivory p-8 md:p-16"
        >
          <div className="flex justify-between items-start gap-6 mb-8">
            <div>
              <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-widest">
                <span style={{ color: "var(--copper)" }}>{newsCategoryLabel(news.category)}</span>
                {news.date && <span className="text-[var(--brown)]/60">· {formatDate(news.date)}</span>}
              </div>
              <h2 className="font-display text-4xl md:text-6xl mt-3 text-[var(--ink)] leading-tight">{news.title}</h2>
            </div>
            <button
              onClick={onClose}
              data-testid="article-close"
              className="text-[var(--ink)] font-mono text-[11px] uppercase tracking-widest border border-[var(--line)] px-4 py-2 hover:bg-[var(--ink)] hover:text-[var(--ivory)] transition-colors"
            >
              Fermer ✕
            </button>
          </div>

          {news.image_url && (
            <div className="w-full aspect-[4/3] overflow-hidden bg-[var(--ink)] mb-10">
              <SafeImage src={news.image_url} alt={news.title} className="w-full h-full object-cover object-center" />
            </div>
          )}

          {news.summary && (
            <p className="font-display italic text-2xl md:text-3xl leading-snug text-[var(--brown)] max-w-3xl border-l-2 border-[var(--copper)] pl-6 mb-10">
              {news.summary}
            </p>
          )}

          {news.content && (
            <div className="text-[16px] leading-[1.9] text-[var(--brown)] max-w-3xl whitespace-pre-line font-body">
              {news.content}
            </div>
          )}

          {news.gallery && news.gallery.length > 0 && (
            <div className="mt-16">
              <p className="eyebrow mb-6" style={{ color: "var(--copper)" }}>Documents de recherche</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {news.gallery.map((g, i) => (
                  <figure key={i} className="space-y-3">
                    <div className="border border-[var(--line)] bg-[var(--ink)]">
                      <SafeImage src={g.src} alt={g.caption} className="w-full h-auto" />
                    </div>
                    {g.caption && <figcaption className="text-[13px] text-[var(--brown)]/85 leading-relaxed font-mono">{g.caption}</figcaption>}
                  </figure>
                ))}
              </div>
            </div>
          )}

          {news.tags && news.tags.length > 0 && (
            <div className="mt-14 pt-8 border-t border-[var(--line)] flex flex-wrap gap-2">
              {news.tags.map((t) => (
                <span key={t} className="text-[10px] font-mono uppercase tracking-widest px-3 py-1 border border-[var(--copper)]/40 text-[var(--copper)]">
                  {t}
                </span>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}
